import { gsap } from "../gsap.js";
import Swiper from "swiper";
import { Navigation } from "swiper/modules";

export default function initSearchMegaMenu() {
  const navbarContainer = document.querySelector(".navbar-container");
  if (!navbarContainer) return;

  const searchBtn = navbarContainer.querySelector(".search-menu-btn");
  const megaMenu = navbarContainer.querySelector(".search-mega-menu");
  if (!searchBtn || !megaMenu) return;

  const navbarWrapper = document.querySelector(".navbar-wrapper");
  const field = megaMenu.querySelector(".search-box-field");
  const defaultContent = megaMenu.querySelector(".search-mega-menu-default");
  const suggestionContent = megaMenu.querySelector(
    ".search-mega-menu-suggestion"
  );
  const closeBtn = megaMenu.querySelector(".search-box-close");
  const cancelBtn = megaMenu.querySelector(".search-mega-menu-cancel");

  if (!field || !defaultContent || !suggestionContent) return;

  const LG_BREAKPOINT = 1200;
  let isOpen = false;
  let isMobileOpen = false;
  let defaultOpenHeight = 0;

  // --- Search swiper ---
  const swiperContainer = megaMenu.querySelector(".search-swiper-container");
  if (swiperContainer) {
    const swiperEl = swiperContainer.querySelector(".search-swiper");
    const prevButton = swiperContainer.querySelector(".carousel-button-prev");
    const nextButton = swiperContainer.querySelector(".carousel-button-next");
    const progressBar = swiperContainer.querySelector(".carousel-progress-bar");
    const controls = swiperContainer.querySelector(".carousel-controls");

    if (swiperEl) {
      function updateProgressBar(swiper) {
        if (!progressBar) return;
        const total = swiper.slides.length;
        const perView =
          typeof swiper.params.slidesPerView === "number"
            ? swiper.params.slidesPerView
            : 1;
        const visible = Math.min(perView / total, 1);
        const scrollProgress = Math.min(Math.max(swiper.progress, 0), 1);
        const progress = (visible + scrollProgress * (1 - visible)) * 100;
        progressBar.style.width = `${Math.min(progress, 100)}%`;
      }

      function updateControlsVisibility(swiper) {
        if (!controls) return;
        const slidesPerView = swiper.params.slidesPerView;
        const totalSlides = swiper.slides.length;
        if (totalSlides <= slidesPerView) {
          controls.style.display = "none";
        } else {
          controls.style.display = "";
        }
      }

      const endSpacing =
        parseFloat(getComputedStyle(navbarContainer).paddingRight) || 32;

      new Swiper(swiperEl, {
        modules: [Navigation],
        slidesPerView: 1.5,
        spaceBetween: 8,
        slidesOffsetAfter: endSpacing,
        navigation: {
          prevEl: prevButton,
          nextEl: nextButton,
        },
        breakpoints: {
          1200: {
            slidesPerView: 2.5,
          },
        },
        on: {
          init: function () {
            updateProgressBar(this);
            updateControlsVisibility(this);
          },
          slideChange: function () {
            updateProgressBar(this);
          },
          progress: function () {
            updateProgressBar(this);
          },
          resize: function () {
            updateProgressBar(this);
            updateControlsVisibility(this);
          },
        },
      });
    }
  }

  // Set initial state for desktop
  gsap.set(megaMenu, {
    height: 0,
    paddingTop: 0,
    paddingBottom: 0,
  });

  gsap.set(defaultContent, { opacity: 0 });
  gsap.set(suggestionContent, { opacity: 0 });

  // --- Desktop mega menu ---
  function showMegaMenu() {
    isOpen = true;
    searchBtn.classList.add("active");
    if (navbarWrapper) navbarWrapper.classList.add("has-open");

    gsap.set(megaMenu, {
      height: "auto",
      paddingTop:
        "calc(var(--spacing-xl) + var(--spacing-lg) + var(--spacing-sm))",
      paddingBottom: "var(--spacing-xl)",
    });
    // Always measure with default content to get baseline height
    gsap.set(defaultContent, { display: "", opacity: 1 });
    gsap.set(suggestionContent, { display: "none" });
    const defaultHeight = megaMenu.offsetHeight;
    defaultOpenHeight = defaultHeight;

    const hasSuggestion = field.value.trim().length > 0;
    if (hasSuggestion) {
      gsap.set(defaultContent, { display: "none", opacity: 0 });
      gsap.set(suggestionContent, { display: "block", opacity: 1 });
    }
    const targetHeight = hasSuggestion
      ? Math.max(defaultHeight, MIN_SUGGESTION_HEIGHT)
      : defaultHeight;
    gsap.set(megaMenu, {
      height: 0,
      paddingTop: 0,
      paddingBottom: 0,
    });

    const activeContent = hasSuggestion ? suggestionContent : defaultContent;

    const timeline = gsap.timeline();
    timeline.to(megaMenu, {
      height: targetHeight,
      paddingTop:
        "calc(var(--spacing-xl) + var(--spacing-lg) + var(--spacing-sm))",
      paddingBottom: "var(--spacing-xl)",
      duration: 0.3,
      ease: "power2.out",
    });
    timeline.to(
      activeContent,
      {
        opacity: 1,
        duration: 0.2,
        ease: "power1.out",
      },
      "-=0.15"
    );
  }

  function hideMegaMenu() {
    isOpen = false;
    searchBtn.classList.remove("active");
    if (navbarWrapper) navbarWrapper.classList.remove("has-open");

    const activeContent =
      field.value.trim().length > 0 ? suggestionContent : defaultContent;

    const timeline = gsap.timeline();
    timeline.to(activeContent, {
      opacity: 0,
      duration: 0.15,
      ease: "power1.in",
    });
    timeline.to(megaMenu, {
      height: 0,
      paddingTop: 0,
      paddingBottom: 0,
      duration: 0.25,
      ease: "power2.in",
    });
  }

  // --- Mobile search sidebar ---
  function showMobileSearch() {
    isMobileOpen = true;
    document.body.classList.add("search-open");

    // Clear desktop GSAP inline styles for mobile
    gsap.set(megaMenu, {
      clearProps: "height,paddingTop,paddingBottom",
    });

    const contentEl =
      field.value.trim().length > 0 ? suggestionContent : defaultContent;
    gsap.set(contentEl, { opacity: 1 });

    const timeline = gsap.timeline();
    timeline.to(megaMenu, {
      y: "0%",
      duration: 0.4,
      ease: "power2.out",
    });
    timeline.to(
      megaMenu.querySelector(".search-mega-menu-field"),
      {
        opacity: 1,
        duration: 0.2,
        ease: "power1.out",
      },
      "-=0.15"
    );

    // Focus search field after animation
    timeline.call(() => field.focus());
  }

  function hideMobileSearch() {
    isMobileOpen = false;

    const timeline = gsap.timeline();
    timeline.to(megaMenu, {
      y: "-100%",
      duration: 0.4,
      ease: "power2.in",
      onComplete: () => {
        document.body.classList.remove("search-open");
        field.value = "";
        toggleContent();
      },
    });
  }

  const MIN_SUGGESTION_HEIGHT = 500;

  // Recalculate and animate mega menu height for desktop
  function updateMegaMenuHeight() {
    if (!isOpen || window.innerWidth < LG_BREAKPOINT) return;

    const hasSuggestion = field.value.trim().length > 0;

    // For suggestion: at least 500px or default height (whichever is larger)
    // For default: use saved default height
    const targetHeight = hasSuggestion
      ? Math.max(defaultOpenHeight, MIN_SUGGESTION_HEIGHT)
      : defaultOpenHeight;

    gsap.to(megaMenu, {
      height: targetHeight,
      duration: 0.25,
      ease: "power2.out",
    });
  }

  // Toggle default/suggestion content based on field value
  function toggleContent() {
    const hasValue = field.value.trim().length > 0;
    if (hasValue) {
      gsap.set(defaultContent, { display: "none", opacity: 0 });
      gsap.set(suggestionContent, { display: "block", opacity: 1 });
    } else {
      gsap.set(defaultContent, { display: "", opacity: 1 });
      gsap.set(suggestionContent, { display: "none", opacity: 0 });
    }
    updateMegaMenuHeight();
  }

  field.addEventListener("input", toggleContent);

  if (closeBtn) {
    closeBtn.addEventListener("click", (e) => {
      e.preventDefault();
      field.value = "";
      toggleContent();
      field.focus();
    });
  }

  // Cancel button (mobile)
  if (cancelBtn) {
    cancelBtn.addEventListener("click", (e) => {
      e.preventDefault();
      hideMobileSearch();
    });
  }

  // Search button click
  searchBtn.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (window.innerWidth < LG_BREAKPOINT) {
      if (isMobileOpen) {
        hideMobileSearch();
      } else {
        showMobileSearch();
      }
      return;
    }

    // Desktop: toggle search mega menu
    const openNavItem = navbarContainer.querySelector(".has-item.open");
    if (openNavItem) {
      const navBtn = openNavItem.querySelector(".nav-btn");
      if (navBtn) navBtn.click();
    }

    if (isOpen) {
      hideMegaMenu();
    } else {
      showMegaMenu();
    }
  });

  // Close when clicking outside (desktop only)
  document.addEventListener("click", (e) => {
    if (
      isOpen &&
      !megaMenu.contains(e.target) &&
      !searchBtn.contains(e.target)
    ) {
      hideMegaMenu();
    }
  });

  // Close search mega menu when a navbar mega menu opens
  const navBtns = navbarContainer.querySelectorAll(".has-item .nav-btn");
  navBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      if (isOpen) {
        hideMegaMenu();
      }
    });
  });

  // --- Reset on breakpoint change ---
  const lgMql = window.matchMedia(`(min-width: ${LG_BREAKPOINT}px)`);

  function resetOnBreakpoint(e) {
    // Kill any running GSAP animations
    gsap.killTweensOf(megaMenu);
    gsap.killTweensOf(defaultContent);
    gsap.killTweensOf(suggestionContent);

    // Reset state flags and classes
    if (isOpen) {
      isOpen = false;
      searchBtn.classList.remove("active");
      if (navbarWrapper) navbarWrapper.classList.remove("has-open");
    }
    if (isMobileOpen) {
      isMobileOpen = false;
      document.body.classList.remove("search-open");
      field.value = "";
    }

    // Crossed from desktop → mobile
    if (!e.matches) {
      // Clear desktop inline styles, let CSS handle mobile layout
      gsap.set(megaMenu, {
        clearProps: "height,paddingTop,paddingBottom",
      });
    }

    // Crossed from mobile → desktop
    if (e.matches) {
      // Clear mobile inline styles, reset to desktop initial state
      gsap.set(megaMenu, {
        clearProps: "y,transform",
      });
      gsap.set(megaMenu, {
        height: 0,
        paddingTop: 0,
        paddingBottom: 0,
      });
    }

    gsap.set(defaultContent, { opacity: 0 });
    gsap.set(suggestionContent, { opacity: 0 });
    toggleContent();
  }

  lgMql.addEventListener("change", resetOnBreakpoint);
}
